/**
 * 모던 사이드바 컴포넌트
 * 새로운 디자인 시스템을 적용한 사이드바
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  BarChart3,
  Users,
  CreditCard,
  LogOut,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { colors, typography, spacing, borderRadius, shadows, animations, gradients, zIndex } from '../../styles/designTokens';
import { Tooltip } from './Tooltip';

// ===== 타입 정의 =====
export interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  adminInfo?: {
    admin_name?: string;
    admin_id?: string;
  } | null;
  onLogout?: () => void;
}

export interface SidebarMenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path: string;
  badge?: string | number;
}

// ===== 스타일드 컴포넌트 =====
const SidebarContainer = styled.aside<{ $collapsed: boolean }>`
  width: ${({ $collapsed }) => $collapsed ? '80px' : '280px'};
  height: 100vh;
  background: linear-gradient(180deg, ${colors.semantic.background.primary} 0%, ${colors.semantic.background.secondary} 100%);
  border-right: 1px solid ${colors.semantic.border.primary};
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: ${zIndex.desktopSidebar};
  transition: width ${animations.duration.normal} ${animations.easing.smooth};
  box-shadow: ${shadows.lg};
  overflow: hidden;
  
  /* 태블릿에서는 접힌 상태로 표시 */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 80px;
  }
  
  /* 모바일에서는 완전히 숨김 (오버레이 사이드바 사용) */
  @media (max-width: 767px) {
    display: none;
  }
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.semantic.border.secondary};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.semantic.text.tertiary};
  }
`;

const SidebarHeader = styled.div<{ $collapsed: boolean }>`
  padding: ${spacing[6]} ${spacing[4]};
  border-bottom: 1px solid ${colors.semantic.border.primary};
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'space-between'};
  min-height: 80px;
`;

const LogoContainer = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  
  ${({ $collapsed }) => $collapsed && css`
    flex-direction: column;
    gap: ${spacing[2]};
  `}
`;

const LogoImage = styled.img<{ $collapsed: boolean }>`
  width: ${({ $collapsed }) => $collapsed ? '32px' : '40px'};
  height: ${({ $collapsed }) => $collapsed ? '32px' : '40px'};
  object-fit: contain;
  border-radius: ${borderRadius.md};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
`;

const LogoText = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => $collapsed ? 'none' : 'block'};
  
  h1 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.semantic.text.primary};
    margin: 0;
    background: ${gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    font-size: ${typography.fontSize.xs};
    color: ${colors.semantic.text.tertiary};
    margin: 0;
    font-weight: ${typography.fontWeight.medium};
  }
`;

const ToggleButton = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  background: ${colors.semantic.background.primary};
  color: ${colors.semantic.text.secondary};
  cursor: pointer;
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  
  &:hover {
    background: ${colors.semantic.background.tertiary};
    border-color: ${colors.primary[300]};
    color: ${colors.primary[600]};
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
    transition: all ${animations.duration.fast} ${animations.easing.smooth};
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: ${spacing[4]} 0;
`;

const MenuSection = styled.div`
  margin-bottom: ${spacing[6]};
`;

const MenuTitle = styled.h3<{ $collapsed: boolean }>`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${spacing[3]} 0;
  padding: 0 ${spacing[4]};
  display: ${({ $collapsed }) => $collapsed ? 'none' : 'block'};
`;

const MenuList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const MenuItem = styled.li`
  margin: 0 ${spacing[2]};
  margin-bottom: ${spacing[1]};
`;

const MenuLink = styled(NavLink) <{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
  gap: ${({ $collapsed }) => $collapsed ? '0' : spacing[3]};
  padding: ${({ $collapsed }) => $collapsed ? spacing[3] : `${spacing[3]} ${spacing[4]}`};
  border-radius: ${borderRadius.lg};
  text-decoration: none;
  color: ${colors.semantic.text.secondary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  position: relative;
  overflow: hidden;
  min-height: 48px;
  width: 100%;
  
  /* 호버 효과 */
  &:hover {
    background: ${colors.primary[50]};
    color: ${colors.primary[700]};
    transform: ${({ $collapsed }) => $collapsed ? 'scale(1.05)' : 'translateX(4px)'};
    
    svg {
      color: ${colors.primary[600]};
      transform: ${({ $collapsed }) => $collapsed ? 'scale(1.1)' : 'scale(1.05)'};
    }
  }
  
  /* 활성 상태 */
  &.active {
    background: linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%);
    color: ${colors.semantic.text.inverse};
    box-shadow: ${shadows.primary};
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: ${colors.semantic.text.inverse};
      border-radius: 0 ${borderRadius.sm} ${borderRadius.sm} 0;
    }
    
    svg {
      color: ${colors.semantic.text.inverse};
    }
  }
  
  /* 아이콘 스타일 */
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    transition: all ${animations.duration.fast} ${animations.easing.smooth};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* 텍스트 스타일 */
  .menu-text {
    display: ${({ $collapsed }) => $collapsed ? 'none' : 'block'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: ${({ $collapsed }) => $collapsed ? '0' : '140px'};
    transition: all ${animations.duration.fast} ${animations.easing.smooth};
    opacity: ${({ $collapsed }) => $collapsed ? '0' : '1'};
  }
  
  /* 배지 스타일 */
  .menu-badge {
    display: ${({ $collapsed }) => $collapsed ? 'none' : 'flex'};
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 ${spacing[2]};
    background: ${colors.error[500]};
    color: ${colors.semantic.text.inverse};
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeight.bold};
    border-radius: ${borderRadius.full};
    margin-left: auto;
  }
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:hover {
      transform: none !important;
    }
    
    svg {
      transform: none !important;
    }
  }
`;

const UserSection = styled.div<{ $collapsed: boolean }>`
  margin-top: auto;
  padding: ${spacing[4]};
  border-top: 1px solid ${colors.semantic.border.primary};
`;

const UserInfo = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]};
  background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[100]} 100%);
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.primary[200]};
  margin-bottom: ${spacing[3]};
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  
  &:hover {
    background: linear-gradient(135deg, ${colors.primary[100]} 0%, ${colors.primary[200]} 100%);
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.semantic.text.inverse};
  font-weight: ${typography.fontWeight.bold};
  font-size: ${typography.fontSize.sm};
  flex-shrink: 0;
`;

const UserDetails = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => $collapsed ? 'none' : 'block'};
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.primary};
  margin-bottom: ${spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.semantic.text.tertiary};
  font-weight: ${typography.fontWeight.medium};
`;

const LogoutButton = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.error[200]};
  border-radius: ${borderRadius.lg};
  background: ${colors.semantic.background.primary};
  color: ${colors.error[600]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  
  &:hover {
    background: ${colors.error[50]};
    border-color: ${colors.error[300]};
    color: ${colors.error[700]};
    transform: translateY(-1px);
    box-shadow: ${shadows.error};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
  
  .button-text {
    display: ${({ $collapsed }) => $collapsed ? 'none' : 'block'};
  }
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:hover {
      transform: none !important;
    }
  }
`;

// ===== 메뉴 데이터 =====
const menuItems: SidebarMenuItem[] = [
  { id: 'dashboard', label: '대시보드', icon: BarChart3, path: '/dashboard' },
  { id: 'customers', label: '고객 관리', icon: Users, path: '/customers' },
  { id: 'payments', label: '결제 관리', icon: CreditCard, path: '/payments' },
];

// ===== 메인 컴포넌트 =====
export const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onToggle,
  adminInfo,
  onLogout
}) => {
  const location = useLocation();

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <SidebarContainer $collapsed={collapsed}>
      {/* 헤더 */}
      <SidebarHeader $collapsed={collapsed}>
        <LogoContainer $collapsed={collapsed}>
          <LogoImage
            src="/images/logo.png"
            alt="BRANDINSIGHT Logo"
            $collapsed={collapsed}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <LogoText $collapsed={collapsed}>
            <h1>BRANDINSIGHT</h1>
            <p>Admin Dashboard</p>
          </LogoText>
        </LogoContainer>

        {onToggle && (
          <ToggleButton $collapsed={collapsed} onClick={onToggle}>
            {collapsed ? <ArrowRight /> : <ArrowLeft />}
          </ToggleButton>
        )}
      </SidebarHeader>

      {/* 메뉴 */}
      <SidebarContent>
        <MenuSection>
          <MenuTitle $collapsed={collapsed}>메인 메뉴</MenuTitle>
          <MenuList>
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const isActive =
                location.pathname === item.path ||
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

              return (
                <MenuItem key={item.id}>
                  {collapsed ? (
                    <Tooltip content={item.label} position="right" delay={200}>
                      <MenuLink
                        to={item.path}
                        $collapsed={collapsed}
                        className={isActive ? 'active' : ''}
                      >
                        <IconComponent />
                        <span className="menu-text">{item.label}</span>
                        {item.badge && (
                          <span className="menu-badge">{item.badge}</span>
                        )}
                      </MenuLink>
                    </Tooltip>
                  ) : (
                    <MenuLink
                      to={item.path}
                      $collapsed={collapsed}
                      className={isActive ? 'active' : ''}
                    >
                      <IconComponent />
                      <span className="menu-text">{item.label}</span>
                      {item.badge && (
                        <span className="menu-badge">{item.badge}</span>
                      )}
                    </MenuLink>
                  )}
                </MenuItem>
              );
            })}
          </MenuList>
        </MenuSection>
      </SidebarContent>

      {/* 사용자 정보 및 로그아웃 */}
      <UserSection $collapsed={collapsed}>
        {adminInfo && (
          <UserInfo $collapsed={collapsed}>
            <UserAvatar>
              {adminInfo.admin_name?.charAt(0) || 'A'}
            </UserAvatar>
            <UserDetails $collapsed={collapsed}>
              <UserName>{adminInfo.admin_name || '관리자'}</UserName>
              <UserRole>{adminInfo.admin_id || 'admin'}</UserRole>
            </UserDetails>
          </UserInfo>
        )}

        {collapsed ? (
          <Tooltip content="로그아웃" position="right" delay={200}>
            <LogoutButton $collapsed={collapsed} onClick={handleLogout}>
              <LogOut />
              <span className="button-text">로그아웃</span>
            </LogoutButton>
          </Tooltip>
        ) : (
          <LogoutButton $collapsed={collapsed} onClick={handleLogout}>
            <LogOut />
            <span className="button-text">로그아웃</span>
          </LogoutButton>
        )}
      </UserSection>
    </SidebarContainer>
  );
};

export default Sidebar;
